import { useState } from 'react'
import { Settings, Sun, Moon, User, Zap, BarChart3, Check, X as XIcon, Mail, Lock, Eye, EyeOff, LinkIcon, CheckCircle2, Cpu } from 'lucide-react'
import { useSettings, FONT_STEPS, FONT_MIN_STEP, FONT_MAX_STEP, FONT_DEFAULT_STEP } from '@/lib/settings'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import clsx from 'clsx'

const themes = [
  { value: 'light' as const, icon: Sun, label: 'Светлая' },
  { value: 'dark' as const, icon: Moon, label: 'Тёмная' },
]

export default function SettingsPage() {
  const { settings, availableModels, setTheme, increaseFontStep, decreaseFontStep, setFontStep, setSelectedModel } = useSettings()
  const queryClient = useQueryClient()

  const { data: profile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => apiClient.getUserProfile(),
    staleTime: 2 * 60 * 1000,
  })

  const { data: accountStatus } = useQuery({
    queryKey: ['account-status'],
    queryFn: () => apiClient.getAccountStatus(),
    staleTime: 2 * 60 * 1000,
  })

  const [linkEmail, setLinkEmail] = useState('')
  const [linkPassword, setLinkPassword] = useState('')
  const [linkConfirm, setLinkConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [linkLoading, setLinkLoading] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [linkSuccess, setLinkSuccess] = useState(false)

  // "У меня уже есть аккаунт на сайте" — fold this mini-app account into an
  // existing web account via a one-time code generated in the web cabinet.
  const [showCodeForm, setShowCodeForm] = useState(false)
  const [linkCode, setLinkCode] = useState('')
  const [codeLoading, setCodeLoading] = useState(false)
  const [codeError, setCodeError] = useState<string | null>(null)
  const [codeSuccess, setCodeSuccess] = useState(false)

  const canDecrease = settings.fontStep > FONT_MIN_STEP
  const canIncrease = settings.fontStep < FONT_MAX_STEP
  const currentPx = FONT_STEPS[settings.fontStep]
  const isDefault = settings.fontStep === FONT_DEFAULT_STEP

  const authTelegramId = getAuthState().telegramId
  const isPremium = profile?.plan === 'premium'

  // Покупка тарифа ВНУТРИ мини-аппа (без входа на веб): создаём платёж → открываем ЮKassa.
  const { data: tariffs } = useQuery({
    queryKey: ['tariffs-law'],
    queryFn: () => apiClient.getTariffs(),
    enabled: !isPremium,
    staleTime: 60_000,
  })
  const [buying, setBuying] = useState<string | null>(null)
  const buyPlan = async (planId: string) => {
    setBuying(planId)
    try {
      const r = await apiClient.createCheckout(planId)
      const url = r.confirmation_url
      if (url) { const w = window.open(url, '_blank'); if (!w) window.location.href = url }
    } catch { /* noop */ } finally { setBuying(null) }
  }
  const hasRealEmail = accountStatus?.has_real_email ?? false

  const handleLinkAccount = async () => {
    setLinkError(null)
    if (!linkEmail.trim()) { setLinkError('Введите email'); return }
    if (linkPassword.length < 8) { setLinkError('Пароль минимум 8 символов'); return }
    if (linkPassword !== linkConfirm) { setLinkError('Пароли не совпадают'); return }

    setLinkLoading(true)
    try {
      await apiClient.linkAccount(linkEmail.trim(), linkPassword)
      setLinkSuccess(true)
      setLinkEmail('')
      setLinkPassword('')
      setLinkConfirm('')
      queryClient.invalidateQueries({ queryKey: ['account-status'] })
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
    } catch (e) {
      setLinkError(e instanceof Error ? e.message : 'Ошибка привязки')
    } finally {
      setLinkLoading(false)
    }
  }

  const handleLinkByCode = async () => {
    setCodeError(null)
    if (!linkCode.trim()) { setCodeError('Введите код из кабинета на сайте'); return }
    setCodeLoading(true)
    try {
      await apiClient.linkByCode(linkCode.trim())
      setCodeSuccess(true)
      // Re-running init with the same Telegram initData now resolves to the
      // linked web account, so a reload switches the session over cleanly.
      setTimeout(() => { window.location.reload() }, 1600)
    } catch (e) {
      setCodeError(e instanceof Error ? e.message : 'Не удалось привязать аккаунт')
    } finally {
      setCodeLoading(false)
    }
  }

  return (
    <div className="settings-page flex flex-col min-h-full">
      {/* Header */}
      <div className="settings-hero" style={{ padding: '16px 20px 10px' }}>
        <div className="flex items-center" style={{ gap: '10px' }}>
          <div className="rounded-xl glass-icon flex items-center justify-center" style={{ width: '36px', height: '36px' }}>
            <Settings size={18} className="text-telegram-button" />
          </div>
          <h1 className="font-bold" style={{ fontSize: '16px' }}>Настройки</h1>
        </div>
      </div>

      <div className="settings-sections" style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

        {/* Profile section */}
        {profile && (
          <section className="settings-profile">
            <h2 className="font-semibold uppercase tracking-wider text-telegram-hint" style={{ fontSize: '12px', marginBottom: '10px', paddingLeft: '4px' }}>
              Профиль
            </h2>
            <div className="glass-surface" style={{ padding: '14px' }}>
              <div className="flex items-center" style={{ gap: '12px', marginBottom: '12px' }}>
                <div className="rounded-full glass-icon flex items-center justify-center" style={{ width: '40px', height: '40px' }}>
                  <User size={20} className="text-telegram-button" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{profile.name || 'Пользователь'}</p>
                  <p className="text-telegram-hint truncate" style={{ fontSize: '11px' }}>
                    {hasRealEmail ? accountStatus?.email : `Telegram ID: ${authTelegramId || profile.telegram_id || '—'}`}
                  </p>
                </div>
              </div>

              {/* Привязанные платформы (Telegram / ВКонтакте / Веб) */}
              <div className="flex flex-wrap" style={{ gap: '6px', marginBottom: '12px' }}>
                {([['Telegram', !!profile.telegram_linked], ['ВКонтакте', !!profile.vk_linked], ['Веб-сайт', hasRealEmail]] as [string, boolean][]).map(([lbl, on]) => (
                  <span key={lbl} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, padding: '4px 9px', borderRadius: '100px', background: on ? 'rgba(34,197,94,.15)' : 'rgba(127,127,127,.12)', color: on ? '#16a34a' : 'var(--tg-theme-hint-color, #8a93a8)' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: on ? '#22c55e' : '#9aa3b8' }} />
                    {lbl}: {on ? 'привязан' : 'нет'}
                  </span>
                ))}
              </div>

              {/* Linked account status */}
              {hasRealEmail && (
                <div className="flex items-center bg-green-500/10 rounded-lg" style={{ padding: '10px', gap: '8px' }}>
                  <CheckCircle2 size={16} className="text-green-500 shrink-0" />
                  <p className="text-green-700 dark:text-green-400" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                    Аккаунт привязан к {accountStatus?.email}. Вы можете входить на kodeksai.ru
                  </p>
                </div>
              )}

              {/* Link account form */}
              {!hasRealEmail && !linkSuccess && (
                <div className="bg-telegram-button/5 rounded-lg border border-telegram-button/10" style={{ padding: '12px' }}>
                  <div className="flex items-center" style={{ gap: '6px', marginBottom: '10px' }}>
                    <LinkIcon size={14} className="text-telegram-button" />
                    <p className="font-medium text-telegram-text" style={{ fontSize: '12px' }}>
                      Привязать email для входа на kodeksai.ru
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div className="relative">
                      <Mail size={14} className="absolute text-telegram-hint" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                      <input
                        type="email"
                        placeholder="Email"
                        value={linkEmail}
                        onChange={e => { setLinkEmail(e.target.value); setLinkError(null) }}
                        className="w-full bg-telegram-bg rounded-lg text-telegram-text placeholder:text-telegram-hint/50 border border-telegram-hint/15 focus:border-telegram-button focus:outline-none"
                        style={{ padding: '10px 10px 10px 32px', fontSize: '13px' }}
                        autoComplete="email"
                      />
                    </div>
                    <div className="relative">
                      <Lock size={14} className="absolute text-telegram-hint" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Пароль (мин. 8 символов)"
                        value={linkPassword}
                        onChange={e => { setLinkPassword(e.target.value); setLinkError(null) }}
                        className="w-full bg-telegram-bg rounded-lg text-telegram-text placeholder:text-telegram-hint/50 border border-telegram-hint/15 focus:border-telegram-button focus:outline-none"
                        style={{ padding: '10px 36px 10px 32px', fontSize: '13px' }}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute text-telegram-hint"
                        style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                      >
                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    <div className="relative">
                      <Lock size={14} className="absolute text-telegram-hint" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Подтвердите пароль"
                        value={linkConfirm}
                        onChange={e => { setLinkConfirm(e.target.value); setLinkError(null) }}
                        className="w-full bg-telegram-bg rounded-lg text-telegram-text placeholder:text-telegram-hint/50 border border-telegram-hint/15 focus:border-telegram-button focus:outline-none"
                        style={{ padding: '10px 10px 10px 32px', fontSize: '13px' }}
                        autoComplete="new-password"
                      />
                    </div>

                    {linkError && (
                      <p className="text-red-500" style={{ fontSize: '11px' }}>{linkError}</p>
                    )}

                    <button
                      onClick={handleLinkAccount}
                      disabled={linkLoading}
                      className={clsx(
                        'w-full rounded-lg font-medium text-white transition-all active:scale-[0.98]',
                        linkLoading ? 'bg-telegram-button/50' : 'bg-telegram-button'
                      )}
                      style={{ padding: '10px', fontSize: '13px' }}
                    >
                      {linkLoading ? 'Привязка...' : 'Привязать аккаунт'}
                    </button>
                  </div>
                </div>
              )}

              {/* Success message */}
              {linkSuccess && !hasRealEmail && (
                <div className="flex items-center bg-green-500/10 rounded-lg" style={{ padding: '10px', gap: '8px' }}>
                  <CheckCircle2 size={16} className="text-green-500 shrink-0" />
                  <p className="text-green-700 dark:text-green-400" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                    Аккаунт успешно привязан! Теперь вы можете входить на kodeksai.ru
                  </p>
                </div>
              )}

              {/* "У меня уже есть аккаунт на сайте" — fold into existing web account by code */}
              {!hasRealEmail && !linkSuccess && !codeSuccess && (
                <div style={{ marginTop: '10px' }}>
                  {!showCodeForm ? (
                    <button
                      onClick={() => { setShowCodeForm(true); setCodeError(null) }}
                      className="w-full text-telegram-button"
                      style={{ fontSize: '12px', padding: '8px', fontWeight: 500 }}
                    >
                      У меня уже есть аккаунт на сайте →
                    </button>
                  ) : (
                    <div className="bg-telegram-button/5 rounded-lg border border-telegram-button/10" style={{ padding: '12px' }}>
                      <p className="text-telegram-hint" style={{ fontSize: '11px', lineHeight: '1.45', marginBottom: '10px' }}>
                        Войдите на app.kodeksai.ru → «Настройки» → «Привязать Telegram», получите код и введите его здесь. Этот Telegram станет частью вашего основного аккаунта — чаты, документы и подписка будут общими.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <input
                          type="text"
                          placeholder="Код из кабинета"
                          value={linkCode}
                          onChange={e => { setLinkCode(e.target.value); setCodeError(null) }}
                          className="w-full bg-telegram-bg rounded-lg text-telegram-text placeholder:text-telegram-hint/50 border border-telegram-hint/15 focus:border-telegram-button focus:outline-none"
                          style={{ padding: '10px', fontSize: '13px' }}
                          autoComplete="one-time-code"
                        />
                        {codeError && <p className="text-red-500" style={{ fontSize: '11px' }}>{codeError}</p>}
                        <button
                          onClick={handleLinkByCode}
                          disabled={codeLoading}
                          className={clsx(
                            'w-full rounded-lg font-medium text-white transition-all active:scale-[0.98]',
                            codeLoading ? 'bg-telegram-button/50' : 'bg-telegram-button'
                          )}
                          style={{ padding: '10px', fontSize: '13px' }}
                        >
                          {codeLoading ? 'Привязка...' : 'Объединить аккаунты'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Code-link success */}
              {codeSuccess && (
                <div className="flex items-center bg-green-500/10 rounded-lg" style={{ padding: '10px', gap: '8px', marginTop: '10px' }}>
                  <CheckCircle2 size={16} className="text-green-500 shrink-0" />
                  <p className="text-green-700 dark:text-green-400" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                    Аккаунты объединены! Обновляем приложение…
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Plan section */}
        {profile && (
          <section className="settings-plan">
            <h2 className="font-semibold uppercase tracking-wider text-telegram-hint" style={{ fontSize: '12px', marginBottom: '10px', paddingLeft: '4px' }}>
              Тарифный план
            </h2>
            <div className="glass-surface" style={{ padding: '14px' }}>
              <div className="flex items-center justify-between" style={{ marginBottom: '12px' }}>
                <div className="flex items-center" style={{ gap: '8px' }}>
                  <Zap size={16} className={isPremium ? 'text-yellow-500' : 'text-telegram-hint'} />
                  <span className="font-semibold text-sm">{isPremium ? 'Premium' : 'Бесплатный'}</span>
                </div>
                <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full ${
                  isPremium ? 'bg-yellow-500/10 text-yellow-600' : 'bg-telegram-hint/10 text-telegram-hint'
                }`}>
                  {isPremium ? '⭐ Premium' : 'Free'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {[
                  { label: 'Полнотекстовый поиск', enabled: true },
                  { label: 'Семантический поиск', enabled: profile.features.vector_search },
                  { label: 'AI-консультант', enabled: profile.features.agent_chat },
                  { label: 'Анализ документов', enabled: profile.features.doc_analysis },
                ].map(({ label, enabled }) => (
                  <div key={label} className="flex items-center" style={{ gap: '8px' }}>
                    {enabled
                      ? <Check size={14} className="text-green-500" />
                      : <XIcon size={14} className="text-telegram-hint/40" />
                    }
                    <span className={`text-xs ${enabled ? 'text-telegram-text' : 'text-telegram-hint/50'}`}>{label}</span>
                  </div>
                ))}
              </div>

              {!isPremium && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                  {(tariffs?.plans ?? []).map((pl) => (
                    <button
                      key={pl.plan_id}
                      onClick={() => buyPlan(pl.plan_id)}
                      disabled={buying === pl.plan_id}
                      className="w-full py-2.5 px-3 rounded-xl bg-telegram-button text-telegram-button-text text-sm font-semibold active:scale-[0.98] transition-all flex items-center justify-between gap-1.5 disabled:opacity-60"
                    >
                      <span className="flex items-center gap-1.5"><Zap size={15} /> {buying === pl.plan_id ? 'Открываем оплату…' : `Оформить «${pl.title}»`}</span>
                      <span>{pl.amount_rub} ₽ / {pl.period_days} дн.</span>
                    </button>
                  ))}
                  <p className="text-[11px] text-telegram-hint text-center" style={{ marginTop: '2px' }}>
                    Оплата картой через ЮKassa. Доступ откроется после оплаты на этом аккаунте.
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* AI Model section */}
        <section className="settings-models">
          <h2 className="font-semibold uppercase tracking-wider text-telegram-hint" style={{ fontSize: '12px', marginBottom: '10px', paddingLeft: '4px' }}>
            Модель ИИ
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {availableModels.map(model => {
              const isSelected = settings.selectedModel === model.id
              return (
                <button
                  key={model.id}
                  onClick={() => setSelectedModel(model.id)}
                  className={clsx(
                    'glass-surface w-full text-left transition-all active:scale-[0.98]',
                    isSelected && 'ring-1.5 ring-telegram-button/40'
                  )}
                  style={{ padding: '12px 14px', border: isSelected ? '1.5px solid var(--tg-theme-button-color, #3b82f6)' : '1px solid var(--glass-border)' }}
                >
                  <div className="flex items-center" style={{ gap: '10px' }}>
                    <div className={clsx(
                      'rounded-xl flex items-center justify-center shrink-0',
                      isSelected ? 'bg-telegram-button text-white' : 'glass-icon text-telegram-hint'
                    )} style={{ width: '32px', height: '32px' }}>
                      <Cpu size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center" style={{ gap: '6px' }}>
                        <span className="font-medium text-sm">{model.name}</span>
                        {model.cost === 'free' && (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-green-500/10 text-green-600">FREE</span>
                        )}
                      </div>
                      <p className="text-telegram-hint" style={{ fontSize: '11px' }}>{model.description}</p>
                    </div>
                    {isSelected && <Check size={16} className="text-telegram-button shrink-0" />}
                  </div>
                </button>
              )
            })}
          </div>
        </section>

        {/* Usage section */}
        {profile && (
          <section className="settings-usage">
            <h2 className="font-semibold uppercase tracking-wider text-telegram-hint" style={{ fontSize: '12px', marginBottom: '10px', paddingLeft: '4px' }}>
              Использование
            </h2>
            <div className="glass-surface" style={{ padding: '14px' }}>
              <div className="flex items-center" style={{ gap: '8px', marginBottom: '10px' }}>
                <BarChart3 size={16} className="text-telegram-button" />
                <span className="font-medium text-sm">Сегодня</span>
              </div>
              <div className="grid grid-cols-2" style={{ gap: '8px' }}>
                <div className="glass-icon rounded-xl" style={{ padding: '10px' }}>
                  <p className="text-telegram-hint" style={{ fontSize: '10px' }}>AI-запросов</p>
                  <p className="font-bold text-sm">
                    {profile.usage.today_agent_requests}
                    {profile.usage.agent_daily_limit != null && (
                      <span className="font-normal text-telegram-hint"> / {profile.usage.agent_daily_limit}</span>
                    )}
                  </p>
                </div>
                <div className="glass-icon rounded-xl" style={{ padding: '10px' }}>
                  <p className="text-telegram-hint" style={{ fontSize: '10px' }}>Всего запросов</p>
                  <p className="font-bold text-sm">{profile.usage.today_requests}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Theme section */}
        <section className="settings-theme">
          <h2 className="font-semibold uppercase tracking-wider text-telegram-hint" style={{ fontSize: '11px', marginBottom: '8px', paddingLeft: '4px' }}>
            Тема оформления
          </h2>
          <div className="grid grid-cols-2" style={{ gap: '6px' }}>
            {themes.map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                onClick={() => setTheme(value)}
                className={clsx(
                  'flex flex-col items-center rounded-xl transition-all duration-300 active:scale-95',
                  settings.theme === value
                    ? 'bg-telegram-button text-white shadow-lg'
                    : 'glass-surface text-telegram-text'
                )}
                style={{ gap: '4px', padding: '10px 8px' }}
              >
                <Icon size={18} />
                <span className="font-medium" style={{ fontSize: '11px' }}>{label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Font size section */}
        <section className="settings-font">
          <h2 className="font-semibold uppercase tracking-wider text-telegram-hint" style={{ fontSize: '11px', marginBottom: '8px', paddingLeft: '4px' }}>
            Размер шрифта
          </h2>
          <div className="glass-surface" style={{ padding: '12px' }}>
            <div className="flex items-center justify-between">
              <button
                onClick={decreaseFontStep}
                disabled={!canDecrease}
                className={clsx(
                  'rounded-lg flex items-center justify-center transition-all active:scale-90',
                  !canDecrease
                    ? 'glass-icon text-telegram-hint/30'
                    : 'glass-icon text-telegram-button'
                )}
                style={{ width: '36px', height: '36px' }}
              >
                <span style={{ fontSize: '13px', fontWeight: 700 }}>A</span>
                <span style={{ fontSize: '9px', position: 'relative', top: '2px' }}>−</span>
              </button>

              <div className="flex flex-col items-center" style={{ gap: '2px' }}>
                <span className="font-bold text-telegram-text" style={{ fontSize: '16px' }}>{currentPx}px</span>
                <div className="flex items-center" style={{ gap: '2px' }}>
                  {FONT_STEPS.map((_, idx) => (
                    <div
                      key={idx}
                      className={clsx(
                        'rounded-full transition-all',
                        idx <= settings.fontStep ? 'bg-telegram-button' : 'bg-telegram-hint/20'
                      )}
                      style={{ width: idx === settings.fontStep ? '6px' : '4px', height: idx === settings.fontStep ? '6px' : '4px' }}
                    />
                  ))}
                </div>
                {!isDefault && (
                  <button
                    onClick={() => setFontStep(FONT_DEFAULT_STEP)}
                    className="text-telegram-link"
                    style={{ fontSize: '9px' }}
                  >
                    Сбросить
                  </button>
                )}
              </div>

              <button
                onClick={increaseFontStep}
                disabled={!canIncrease}
                className={clsx(
                  'rounded-lg flex items-center justify-center transition-all active:scale-90',
                  !canIncrease
                    ? 'glass-icon text-telegram-hint/30'
                    : 'glass-icon text-telegram-button'
                )}
                style={{ width: '36px', height: '36px' }}
              >
                <span style={{ fontSize: '16px', fontWeight: 700 }}>A</span>
                <span style={{ fontSize: '9px', position: 'relative', top: '-2px' }}>+</span>
              </button>
            </div>
          </div>
        </section>

        {/* Info */}
        <section className="settings-footer text-center" style={{ paddingTop: '4px' }}>
          <p className="text-telegram-hint" style={{ fontSize: '10px' }}>
            Настройки сохраняются на устройстве
          </p>
        </section>
      </div>
    </div>
  )
}
